import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AttributionControl, GeoJSON, Marker as LMarker, Tooltip as LTooltip, MapContainer, Pane, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L, { type LatLngBoundsExpression, type Map as LeafletMap, type PathOptions } from "leaflet";
import { useTheme } from "@tricura/atlas-ui";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet/dist/leaflet.css";
import buttonReset from "../../../../../assets/heatmap/button-reset.svg";
import buttonZoomIn from "../../../../../assets/heatmap/button-zoom-in.svg";
import buttonZoomOut from "../../../../../assets/heatmap/button-zoom-out.svg";
import markerAlmostHigh from "../../../../../assets/heatmap/marker-almost-high.svg";
import markerAlmostHighHover from "../../../../../assets/heatmap/marker-almost-high-hover.svg";
import markerHigh from "../../../../../assets/heatmap/marker-high.svg";
import markerHighHover from "../../../../../assets/heatmap/marker-high-hover.svg";
import markerLow from "../../../../../assets/heatmap/marker-low.svg";
import markerLowHover from "../../../../../assets/heatmap/marker-low-hover.svg";
import markerMedium from "../../../../../assets/heatmap/marker-medium.svg";
import markerMediumHover from "../../../../../assets/heatmap/marker-medium-hover.svg";
import markerWeak from "../../../../../assets/heatmap/marker-weak-match.svg";
import markerWeakHover from "../../../../../assets/heatmap/marker-weak-match-hover.svg";
import type { MapMarker, MarkerRiskLevel } from "../../../../../types/litigation";
import { STATE_FEATURES, STATE_LABELS } from "../state-outlines";
import { type MapEngineProps } from "../types";

// ─── Constants ────────────────────────────────────────────────────────────────

/** CARTO basemaps require an API key; without one every tile comes back
 *  with an "API KEY REQUIRED" watermark. The key is public by design
 *  (restricted by domain in the CARTO dashboard) and injected per
 *  environment via `VITE_CARTO_BASEMAPS_KEY`. */
const CARTO_KEY = import.meta.env.VITE_CARTO_BASEMAPS_KEY as string | undefined;
const CARTO_KEY_QUERY = CARTO_KEY ? `?key=${encodeURIComponent(CARTO_KEY)}` : "";
/** Required by the CARTO basemaps terms, on both free and paid tiers. */
const TILE_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>';

/** Basemaps without labels — we draw our own state abbreviations on top
 *  and don't want city labels competing with the data. Positron (light)
 *  and Dark Matter (dark), both the CartoDB `_nolabels` variants, so the
 *  basemap follows the active atlas theme. */
const TILE_URL_LIGHT = `https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png${CARTO_KEY_QUERY}`;
const TILE_URL_DARK = `https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png${CARTO_KEY_QUERY}`;
/** Labels-only overlay (transparent PNGs: street / place / county names).
 *  Layered above the no-labels basemap but only fetched past
 *  `LABELS_MIN_ZOOM` — so the country view stays clean (just our state
 *  abbreviations + pins) and street/city/county names fade in when the UW
 *  zooms to inspect facilities up close (Andressa review, jun-18). */
const TILE_LABELS_LIGHT = `https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png${CARTO_KEY_QUERY}`;
const TILE_LABELS_DARK = `https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png${CARTO_KEY_QUERY}`;
/** Below this zoom no place labels render (keeps the national view clean). */
const LABELS_MIN_ZOOM = 7;
const US_CENTER: [number, number] = [39, -98];
const US_ZOOM = 4;
const US_MIN_ZOOM = 3;
/** Hard wall — combined with maxBoundsViscosity=1.0 the user can't drag
 *  past these. Tight enough that other continents never enter the view. */
const US_BOUNDS: LatLngBoundsExpression = [
    [18, -170],
    [72, -65],
];

// ─── Auto-fit-to-facilities (OR-1081) ───────────────────────────────────────────
// On load the map frames the submission's facilities instead of the whole
// country — but only when they're geographically clustered. If they sprawl
// across the country (or include an Alaska/Hawaii outlier that blows up the
// bounding box) we fall back to the continental-US default view.

/** Above these degree spans the facilities count as "scattered" → US default.
 *  Sized roughly to the continental US (lat ≈ 24–49, lng ≈ -125…-66). */
const SCATTER_LAT_SPAN = 22;
const SCATTER_LNG_SPAN = 45;
/** Padding (px) around the fitted bounds so pins don't hug the edges. */
const FIT_PADDING: [number, number] = [40, 40];
/** Cap the fit zoom so a single (or tightly-clustered) facility doesn't
 *  rocket to street level — keeps regional context around it. */
const FIT_MAX_ZOOM = 8;

type FacilityView = { kind: "us" } | { kind: "bounds"; bounds: L.LatLngBounds };

/** Decide the initial view from the marker set. Pure — no map side effects. */
function computeFacilityView(markers: MapMarker[]): FacilityView {
    const points = markers
        .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng) && !(m.lat === 0 && m.lng === 0))
        .map((m) => [m.lat, m.lng] as [number, number]);

    if (points.length === 0) return { kind: "us" };

    const bounds = L.latLngBounds(points);
    const latSpan = bounds.getNorth() - bounds.getSouth();
    const lngSpan = bounds.getEast() - bounds.getWest();
    if (latSpan > SCATTER_LAT_SPAN || lngSpan > SCATTER_LNG_SPAN) return { kind: "us" };

    return { kind: "bounds", bounds };
}

/** Fallback hex used until the CSS variable resolves on first paint —
 *  matches the atlas-ui token below so the swap is invisible. */
const FALLBACK_STATE_STROKE = "#E5E5E5";
/** atlas-ui token that drives the GeoJSON polygon outline. The states
 *  themselves stay transparent so the basemap shows through. */
const STATE_STROKE_VAR = "--color-border-secondary";

/** One SVG per risk level (Jun-2026 design export), each with its color
 *  baked in. `almost-high` now has its own (orange) asset. The hover
 *  variants add a ring outline — swapped via CSS on hover (see
 *  `.orion-leaflet-pin` rules in styles.css). */
const PIN_ICONS: Record<MarkerRiskLevel, string> = {
    low: markerLow,
    medium: markerMedium,
    "almost-high": markerAlmostHigh,
    high: markerHigh,
    "weak-match": markerWeak,
};

const PIN_ICONS_HOVER: Record<MarkerRiskLevel, string> = {
    low: markerLowHover,
    medium: markerMediumHover,
    "almost-high": markerAlmostHighHover,
    high: markerHighHover,
    "weak-match": markerWeakHover,
};

const RISK_LABEL: Record<MarkerRiskLevel, string> = {
    low: "Low Risk",
    medium: "Medium Risk",
    "almost-high": "Almost High",
    high: "High Risk",
    "weak-match": "Weak Match",
};

/** Pastel-pill triplet used inside the pin tooltip (1px border, no
 *  shadow). Values reference atlas-ui CSS variables so the palette
 *  flips correctly in dark mode without per-engine hex overrides.
 *
 *  Spec source: product Figma (May 2026 refresh). Almost-High shares
 *  the High treatment until the design team specs a distinct tier. */
const RISK_BADGE_STYLE: Record<MarkerRiskLevel, { bg: string; border: string; text: string }> = {
    low: {
        bg: "var(--color-utility-green-50)",
        border: "var(--color-utility-green-200)",
        text: "var(--color-utility-green-700)",
    },
    medium: {
        bg: "var(--color-utility-yellow-50)",
        border: "var(--color-utility-yellow-200)",
        text: "var(--color-utility-yellow-700)",
    },
    // Orange dropped (Andressa + Linda): the risk palette is three solid
    // colors, so High reads red and "almost-high" (no longer produced —
    // see resolveRiskLevel) shares it.
    "almost-high": {
        bg: "var(--color-utility-red-50)",
        border: "var(--color-utility-red-200)",
        text: "var(--color-utility-red-700)",
    },
    high: {
        bg: "var(--color-utility-red-50)",
        border: "var(--color-utility-red-200)",
        text: "var(--color-utility-red-700)",
    },
    "weak-match": {
        bg: "var(--color-utility-neutral-50)",
        border: "var(--color-border-primary)",
        text: "var(--color-utility-neutral-700)",
    },
};

/** Cluster capsule palette — drives the pill that summarises a group
 *  of pins on the map. Replaces the previous dark `#1F1F1F` chip with
 *  the new light treatment per the May 2026 Figma: 2px border on
 *  Weak/Medium/Low (plus a subtle 4px ambient shadow), and a 1px no-
 *  shadow chip on High. Almost-High mirrors High's red palette as
 *  the closest spec'd analogue. */
interface ClusterCapsule {
    bg: string;
    border: string;
    borderWidth: 1 | 2;
    /** Text colour for the count digits inside the capsule. */
    text: string;
    /** Whether the capsule renders with the ambient drop shadow. */
    shadow: boolean;
}

const CLUSTER_CAPSULE_STYLE: Record<MarkerRiskLevel, ClusterCapsule> = {
    "weak-match": {
        bg: "var(--color-utility-neutral-100)",
        border: "var(--color-border-primary)",
        borderWidth: 2,
        text: "var(--color-utility-neutral-700)",
        shadow: true,
    },
    medium: {
        bg: "var(--color-utility-yellow-50)",
        border: "var(--color-utility-yellow-200)",
        borderWidth: 2,
        text: "var(--color-utility-yellow-700)",
        shadow: true,
    },
    low: {
        bg: "var(--color-utility-green-50)",
        border: "var(--color-utility-green-200)",
        borderWidth: 2,
        text: "var(--color-utility-green-700)",
        shadow: true,
    },
    high: {
        bg: "var(--color-utility-red-50)",
        border: "var(--color-utility-red-300)",
        borderWidth: 1,
        text: "var(--color-utility-red-700)",
        shadow: false,
    },
    "almost-high": {
        bg: "var(--color-utility-red-50)",
        border: "var(--color-utility-red-300)",
        borderWidth: 1,
        text: "var(--color-utility-red-700)",
        shadow: false,
    },
};

// ─── State labels (US-state two-letter abbreviations) ────────────────────────

const buildStateLabelIcon = (abbr: string, dark: boolean): L.DivIcon =>
    L.divIcon({
        className: "orion-state-label",
        // Light text + dark halo on the dark basemap; the inverse on the
        // light one — keeps the abbreviations legible over either tile.
        html: `<div style="
            color: ${dark ? "#CBD5E1" : "#475569"};
            font: 600 13px Inter, system-ui, sans-serif;
            text-shadow: ${
                dark
                    ? "0 0 4px rgba(15,23,42,0.85), 0 0 2px rgba(15,23,42,0.85)"
                    : "0 0 4px rgba(255,255,255,0.85), 0 0 2px rgba(255,255,255,0.85)"
            };
            pointer-events: none;
            white-space: nowrap;
            transform: translate(-50%,-50%);
            letter-spacing: 0.5px;
        ">${abbr}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });

// ─── Marker icon factories ────────────────────────────────────────────────────

/** The design markers are drawn on a 40px canvas (halo + dot); the hover
 *  variant adds a ring. Both imgs are stacked and the CSS swaps them on
 *  hover (see `.orion-leaflet-pin` in styles.css) — fixed box so Leaflet
 *  never repositions the marker. */
const PIN_SIZE = 40;

const buildPinIcon = (risk: MarkerRiskLevel): L.DivIcon => {
    const src = PIN_ICONS[risk] ?? PIN_ICONS["weak-match"];
    const hover = PIN_ICONS_HOVER[risk] ?? PIN_ICONS_HOVER["weak-match"];
    return L.divIcon({
        className: "orion-leaflet-pin",
        html: `<img class="pin-normal" src="${src}" width="${PIN_SIZE}" height="${PIN_SIZE}" alt="${risk}" /><img class="pin-hover" src="${hover}" width="${PIN_SIZE}" height="${PIN_SIZE}" alt="" aria-hidden="true" />`,
        iconSize: [PIN_SIZE, PIN_SIZE],
        iconAnchor: [PIN_SIZE / 2, PIN_SIZE / 2],
    });
};

const clusterIconCreate = (cluster: L.MarkerCluster): L.DivIcon => {
    const count = cluster.getChildCount();
    const children = cluster.getAllChildMarkers();

    const counts: Record<string, number> = {};
    for (const child of children) {
        const risk = (child.options as L.MarkerOptions & { riskLevel?: MarkerRiskLevel }).riskLevel ?? "weak-match";
        counts[risk] = (counts[risk] ?? 0) + 1;
    }
    let dominant: MarkerRiskLevel = "weak-match";
    let max = 0;
    for (const [risk, n] of Object.entries(counts)) {
        if (n > max) {
            max = n;
            dominant = risk as MarkerRiskLevel;
        }
    }

    const capsule = CLUSTER_CAPSULE_STYLE[dominant] ?? CLUSTER_CAPSULE_STYLE["weak-match"];
    const height = count < 5 ? 30 : count < 20 ? 38 : 46;
    const paddingX = count < 10 ? 16 : count < 100 ? 20 : 24;
    const fontSize = count < 10 ? 16 : count < 100 ? 18 : 20;
    const width = count < 100 ? 56 : 68;

    return L.divIcon({
        className: "orion-leaflet-cluster",
        html: `
            <div style="
                height: ${height}px;
                padding: 0 ${paddingX}px;
                border-radius: ${height / 2}px;
                background: ${capsule.bg};
                border: ${capsule.borderWidth}px solid ${capsule.border};
                display: flex;
                align-items: center;
                justify-content: center;
                color: ${capsule.text};
                font-size: ${fontSize}px;
                font-weight: 700;
                font-family: Inter, sans-serif;
                line-height: 1;
                white-space: nowrap;
                box-shadow: ${capsule.shadow ? "0 0 4px 0 rgba(0,0,0,0.08)" : "none"};
            ">${count}</div>
        `,
        iconSize: [width, height],
        iconAnchor: [width / 2, height / 2],
    });
};

// ─── Engine ───────────────────────────────────────────────────────────────────

export function LeafletEngine({ markers, height = 480 }: MapEngineProps) {
    const mapRef = useRef<LeafletMap | null>(null);
    const [stateStroke, setStateStroke] = useState<string>(FALLBACK_STATE_STROKE);

    const { theme } = useTheme();
    const isDark =
        theme === "dark" ||
        (theme === "system" && typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches);

    // Re-resolve the atlas-ui token whenever the theme flips so the state
    // outlines track light/dark. `--color-border-secondary` already holds
    // the themed value in CSS; we snapshot it into JS for the GeoJSON
    // stroke (Leaflet styles aren't CSS-variable aware).
    useLayoutEffect(() => {
        const resolved = getComputedStyle(document.documentElement).getPropertyValue(STATE_STROKE_VAR).trim();
        if (resolved) setStateStroke(resolved);
    }, [isDark]);

    const handleZoomIn = useCallback(() => mapRef.current?.zoomIn(), []);
    const handleZoomOut = useCallback(() => mapRef.current?.zoomOut(), []);
    const handleReset = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        // Reset goes back to the same auto-framed view as on load: the
        // facilities' region, or the continental-US default when they're
        // scattered/empty (OR-1081).
        const view = computeFacilityView(markers);
        if (view.kind === "bounds") {
            map.flyToBounds(view.bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 0.5 });
        } else {
            map.flyTo(US_CENTER, US_ZOOM, { duration: 0.5 });
        }
    }, [markers]);

    const stateStyle = useMemo<PathOptions>(
        () => ({
            color: stateStroke,
            weight: 1,
            fillOpacity: 0,
            interactive: false,
        }),
        [stateStroke],
    );

    return (
        <div className="bg-quaternary relative overflow-hidden rounded-lg" style={{ height }}>
            <MapContainer
                center={US_CENTER}
                zoom={US_ZOOM}
                minZoom={US_MIN_ZOOM}
                maxZoom={16}
                // Allow any fractional zoom level — without this Leaflet
                // snaps to whole zooms after every interaction, which is
                // what made Cmd/Ctrl + scroll feel "steppy". With snap
                // disabled the interpolated zoom from the wheel handler
                // (and the +/- buttons) lands precisely where requested.
                zoomSnap={0}
                maxBounds={US_BOUNDS}
                maxBoundsViscosity={1.0}
                worldCopyJump={false}
                zoomControl={false}
                attributionControl={false}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
                ref={(instance) => {
                    mapRef.current = instance;
                }}
            >
                {/* key forces a remount on theme flip so the basemap tiles
                  * swap cleanly between the light and dark variants. */}
                <TileLayer
                    key={isDark ? "dark" : "light"}
                    url={isDark ? TILE_URL_DARK : TILE_URL_LIGHT}
                    attribution={TILE_ATTRIBUTION}
                    noWrap
                />
                {/* Street / city / county labels — only past LABELS_MIN_ZOOM. */}
                <TileLayer
                    key={isDark ? "labels-dark" : "labels-light"}
                    url={isDark ? TILE_LABELS_DARK : TILE_LABELS_LIGHT}
                    minZoom={LABELS_MIN_ZOOM}
                    noWrap
                />
                {/* Bottom-left so it doesn't collide with the risk legend. */}
                <AttributionControl position="bottomleft" prefix={false} />

                <ScrollZoomGate />
                <AutoFitBounds markers={markers} />

                {/* State outlines drawn on top of the basemap so the
                  * polygon strokes (`--color-border-secondary`) read as
                  * crisp dividers between states. */}
                <GeoJSON data={STATE_FEATURES} style={stateStyle} />

                {/* Static state-abbreviation labels — non-interactive, sit
                    above tiles + outlines but below pins. */}
                <Pane name="orion-state-labels" style={{ zIndex: 450 }}>
                    {STATE_LABELS.map(({ fips, abbr, lat, lng }) => (
                        <LMarker
                            key={`label-${fips}`}
                            position={[lat, lng]}
                            icon={buildStateLabelIcon(abbr, isDark)}
                            interactive={false}
                            keyboard={false}
                        />
                    ))}
                </Pane>

                <MarkerClusterGroup
                    chunkedLoading
                    iconCreateFunction={clusterIconCreate}
                    showCoverageOnHover={false}
                    maxClusterRadius={60}
                    spiderfyOnMaxZoom
                >
                    {markers.map((m, i) => (
                        <LeafletPin key={`pin-${i}`} marker={m} />
                    ))}
                </MarkerClusterGroup>
            </MapContainer>

            {/* Zoom + reset controls */}
            <div className="absolute left-3 top-3 z-[500] flex flex-col gap-1">
                <ZoomButton label="Zoom in" onClick={handleZoomIn} src={buttonZoomIn}/>
                <ZoomButton label="Zoom out" onClick={handleZoomOut} src={buttonZoomOut}/>
                <ZoomButton label="Reset" onClick={handleReset} src={buttonReset}/>
            </div>

            {/* Legend (bottom-right) — three SOLID color segments (not a
              * gradient): the design uses three discrete risk colors, so a
              * blended scale (which introduced an orange mid-tone) was
              * wrong. Green / Yellow / Red split at 33 / 66 (Andressa +
              * Linda review). Thresholds are placeholders pending the
              * backend-defined parameters. */}
            <div className="bg-primary border-secondary absolute right-3 bottom-3 z-[500] flex w-[220px] flex-col gap-1.5 rounded-xl border p-3 shadow-sm">
                <div className="text-tertiary flex justify-between text-[11px] font-medium">
                    <span>Low Risk</span>
                    <span>Medium Risk</span>
                    <span>High Risk</span>
                </div>
                <div className="flex h-2 overflow-hidden rounded-full">
                    <span className="flex-1" style={{ background: "#16A34A" }} />
                    <span className="flex-1" style={{ background: "#FACC15" }} />
                    <span className="flex-1" style={{ background: "#DC2626" }} />
                </div>
                <div className="text-quaternary flex justify-between text-[10px] tabular-nums">
                    <span>0</span>
                    <span>33</span>
                    <span>66</span>
                    <span>100</span>
                </div>
            </div>
        </div>
    );
}

// ─── Auto-fit on load ─────────────────────────────────────────────────────────

/** Stable signature for a marker set so we re-fit only when the data
 *  actually changes — not on every render, and not while the user is
 *  panning around their current view. */
function markersSignature(markers: MapMarker[]): string {
    if (markers.length === 0) return "empty";
    const round = (n: number) => Math.round(n * 1000) / 1000;
    const first = markers[0];
    const last = markers[markers.length - 1];
    return `${markers.length}:${round(first.lat)},${round(first.lng)}:${round(last.lat)},${round(last.lng)}`;
}

/** Frame the facilities once per distinct dataset (OR-1081). Mounted inside
 *  <MapContainer> so it can grab the live map via useMap(). */
function AutoFitBounds({ markers }: { markers: MapMarker[] }) {
    const map = useMap();
    const lastFitted = useRef<string | null>(null);

    useEffect(() => {
        const signature = markersSignature(markers);
        if (signature === lastFitted.current) return;
        lastFitted.current = signature;

        const view = computeFacilityView(markers);
        if (view.kind === "bounds") {
            map.fitBounds(view.bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, animate: false });
        } else {
            map.setView(US_CENTER, US_ZOOM, { animate: false });
        }
    }, [map, markers]);

    return null;
}

// ─── Scroll-zoom gate ────────────────────────────────────────────────────────

/** Per-tick zoom step for the wheel handler. Smaller is smoother; 0.5
 *  lands two ticks per integer zoom level which feels glassy on both
 *  a mouse wheel and a MacBook trackpad. Requires `zoomSnap={0}` on
 *  MapContainer so the fractional zooms actually stick. */
const WHEEL_ZOOM_STEP = 0.5;

/** Gate Leaflet's scroll-zoom behind the platform meta key (Cmd on
 *  macOS, Ctrl on Windows/Linux). Bare wheel events propagate so the
 *  page scrolls normally — same UX as Google/Apple Maps embedded on a
 *  page. */
function ScrollZoomGate() {
    const map = useMap();
    useEffect(() => {
        const container = map.getContainer();
        const handler = (e: WheelEvent) => {
            // `metaKey` covers macOS Cmd; `ctrlKey` covers Windows /
            // Linux Ctrl as well as the synthesized ctrlKey that macOS
            // emits on trackpad pinch-zoom, so pinch-to-zoom also lands
            // here.
            if (!(e.ctrlKey || e.metaKey)) return;
            // Block both browser zoom AND page scroll only when we're
            // actually consuming the wheel for map zoom.
            e.preventDefault();
            const delta = e.deltaY > 0 ? -WHEEL_ZOOM_STEP : WHEEL_ZOOM_STEP;
            map.setZoom(map.getZoom() + delta, { animate: true });
        };
        // passive:false is required so preventDefault() takes effect.
        container.addEventListener("wheel", handler, { passive: false });
        return () => container.removeEventListener("wheel", handler);
    }, [map]);
    return null;
}

// ─── Single pin (with popup card) ────────────────────────────────────────────

function LeafletPin({ marker }: { marker: MapMarker }) {
    const risk = marker.riskLevel ?? "weak-match";
    const icon = useMemo(() => buildPinIcon(risk), [risk]);

    // Attach risk to the underlying L.Marker so the cluster icon factory can
    // compute the dominant risk across its children.
    const markerRef = useCallback(
        (instance: L.Marker | null) => {
            if (instance) {
                (instance.options as L.MarkerOptions & { riskLevel?: MarkerRiskLevel }).riskLevel = risk;
            }
        },
        [risk],
    );

    return (
        <LMarker ref={markerRef} position={[marker.lat, marker.lng]} icon={icon}>
            {/* Bottom-right of the pin — matches the legacy V2 placement.
                offset = [right, down] from the marker icon's anchor. */}
            <LTooltip direction="bottom" offset={[130, 8]} opacity={1} className="orion-pin-tooltip">
                <PinTooltipCard marker={marker} />
            </LTooltip>
        </LMarker>
    );
}

function PinTooltipCard({ marker }: { marker: MapMarker }) {
    const risk = marker.riskLevel ?? "weak-match";
    const badge = RISK_BADGE_STYLE[risk];
    const riskLabel = RISK_LABEL[risk];
    const [lawValue, lawSuffix] = (marker.lawsuitsPer100Beds ?? "").split("/");
    const location = [marker.city, marker.state].filter(Boolean).join(", ");

    return (
        <div
            style={{
                width: 280,
                borderRadius: 8,
                background: "var(--color-bg-primary)",
                border: "1px solid var(--color-border-secondary)",
                padding: "12px 14px",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
                fontFamily: "Inter, system-ui, sans-serif",
                color: "var(--color-text-primary)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span
                    style={{
                        borderRadius: 4,
                        border: `1px solid ${badge.border}`,
                        background: badge.bg,
                        color: badge.text,
                        padding: "3px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: 0.2,
                    }}
                >
                    {riskLabel}
                </span>
                {lawValue && (
                    <span style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text-primary)" }}>
                        {lawValue}
                        {lawSuffix && <span style={{ color: "var(--color-text-tertiary)", fontSize: 13, fontWeight: 500 }}>/{lawSuffix}</span>}
                    </span>
                )}
            </div>

            <div
                style={{
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1.3,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    color: "var(--color-text-primary)",
                }}
            >
                {marker.label}
            </div>

            <div style={{ borderTop: "1px solid var(--color-border-secondary)", paddingTop: 8, fontSize: 12, color: "var(--color-text-tertiary)", lineHeight: 1.5 }}>
                {marker.type && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-quaternary)" }}>Type</span>
                        <span style={{ color: "var(--color-text-primary)", fontWeight: 600 }}>{marker.type}</span>
                    </div>
                )}
                {marker.county && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-quaternary)" }}>County</span>
                        <span style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>{marker.county}</span>
                    </div>
                )}
                {location && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-quaternary)" }}>Location</span>
                        <span style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>{location}</span>
                    </div>
                )}
                {marker.licensedBeds > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-quaternary)" }}>Licensed beds</span>
                        <span style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>{marker.licensedBeds}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Zoom button (atlas-token styled) ─────────────────────────────────────────

interface ZoomButtonProps {
    label: string;
    onClick: () => void;
    /** Full button artwork — the SVG ships its own white background,
     *  rounded corners and 1px gray border, so the `<button>` element
     *  itself stays chrome-less and just renders the image. */
    src: string;
}

function ZoomButton({ label, onClick, src}: ZoomButtonProps) {
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            onClick={onClick}
            className="size-8 cursor-pointer outline-none transition duration-100 ease-linear hover:opacity-80 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2"
        >
            <img src={src} alt="" aria-hidden="true" width={32} height={32} className="block" />
        </button>
    );
}
